import { notNil } from '@sixfold/typed-primitives';
import { default as DeckGL } from 'deck.gl';
import React from 'react';

import { createVehiclesLayer, createVehiclesHistoryLayer, createGhostVehicleLayer } from './vehicles_layer';
import { notEmpty } from '../../lib/graphql';
import { Viewport } from '../../map/components/map_viewport';
import { createVehicleBreaksLayer } from '../../tour/components/tour_layer';
import { Vehicle, VehicleHistory, VehicleBreakHistory, VehicleStatus } from '../entities';

interface OverlayVehicle extends Vehicle {
  history?: VehicleHistory[];
  breakHistory?: VehicleBreakHistory[];
  ghost?: VehicleHistory;
  status: VehicleStatus | null;
}

interface Props {
  vehicles: OverlayVehicle[];
  viewport: Viewport;
  onViewportChange(viewport: Viewport): void;
  onLayerClick(coordinates: { lat: number | null; lng: number | null; hdg?: number | null }): void;
}

export const VehiclesOverlay: React.FC<Props> = ({ vehicles, viewport, onViewportChange, onLayerClick }) => {
  const vehiclesLayer = createVehiclesLayer({
    vehicles,
    onClick: (vehicle) => onLayerClick({ lat: vehicle.status.latitude, lng: vehicle.status.longitude }),
  });
  const { pointLayer, pathLayer } = createVehiclesHistoryLayer({ vehicles, onClick: onLayerClick });

  const ghostLayer = vehicles
    .map((vehicle) => vehicle.ghost)
    .filter(notEmpty)
    .map((vehicle, idx) => createGhostVehicleLayer(vehicle, idx));

  const combinedBreakHistory = vehicles
    .map((vehicle) => vehicle.breakHistory)
    .filter(notEmpty)
    .reduce((memo, history) => {
      return [...memo, ...history];
    }, [] as VehicleBreakHistory[]);

  const { breakIconsLayer, breakLabelsLayer } = createVehicleBreaksLayer({
    vehicleBreaks: combinedBreakHistory,
    onBreakClick: onLayerClick,
  });

  return (
    <DeckGL
      width={viewport.width}
      height={viewport.height}
      viewState={{
        longitude: viewport.longitude,
        latitude: viewport.latitude,
        zoom: viewport.zoom,
        pitch: viewport.pitch,
        bearing: viewport.bearing,
        maxZoom: viewport.maxZoom,
      }}
      controller={true}
      onViewStateChange={({ viewState }) =>
        onViewportChange({
          ...viewport,
          longitude: viewState.longitude,
          latitude: viewState.latitude,
          zoom: viewState.zoom,
          pitch: viewState.pitch,
          bearing: viewState.bearing,
        })
      }
      layers={[ghostLayer, pathLayer, pointLayer, breakIconsLayer, breakLabelsLayer, vehiclesLayer].filter(notNil)}
    />
  );
};
